import { useMemo } from "react"
import dayjs, { Dayjs } from "dayjs"
import { LeaveTimelineBar, LeaveTimelineDayFlag } from "./types"

export interface TimelineDay {
  date: Dayjs
  key: string
  isWeekend: boolean
  isMonthStart: boolean
  nonWorking: boolean
  exceedsThreshold: boolean
  absentCount?: number
}

export interface PositionedBar extends LeaveTimelineBar {
  /** 1-based CSS grid column where the bar starts. */
  columnStart: number
  /** Number of day columns the bar covers. */
  span: number
  /** The bar runs past the left edge of the window. */
  clippedStart: boolean
  clippedEnd: boolean
}

/**
 * Expands a date range into day columns once, and resolves each bar to a grid position.
 *
 * Both are memoised because a department-quarter view is roughly 60 rows by 92 days. Rebuilding
 * that on every render — a hover, a tooltip — is the difference between a grid that feels
 * instant and one that stutters.
 */
export function useTimelineDays(
  startDate: string,
  endDate: string,
  dayFlags: LeaveTimelineDayFlag[] = []
) {
  return useMemo<TimelineDay[]>(() => {
    const start = dayjs(startDate).startOf("day")
    const end = dayjs(endDate).startOf("day")

    if (!start.isValid() || !end.isValid() || end.isBefore(start)) {
      return []
    }

    // Indexed once: the loop below probes it per day, and scanning the array each time
    // would make a quarter-long window quadratic.
    const flags = new Map<string, LeaveTimelineDayFlag>()
    dayFlags.forEach((flag) => flags.set(flag.date, flag))

    const days: TimelineDay[] = []
    let cursor = start

    while (!cursor.isAfter(end)) {
      const key = cursor.format("YYYY-MM-DD")
      const flag = flags.get(key)
      const weekday = cursor.day()

      days.push({
        date: cursor,
        key,
        isWeekend: weekday === 0 || weekday === 6,
        isMonthStart: cursor.date() === 1,
        // A weekend is non-working unless the caller says otherwise: companies on a
        // six-day week send explicit flags, and the grid should not contradict the
        // working-day rule the server charged against.
        nonWorking: flag?.nonWorking ?? (weekday === 0 || weekday === 6),
        exceedsThreshold: flag?.exceedsThreshold ?? false,
        absentCount: flag?.absentCount,
      })

      cursor = cursor.add(1, "day")
    }

    return days
  }, [startDate, endDate, dayFlags])
}

/**
 * Places bars on the day grid, clipping any that start before or end after the window
 * rather than dropping them — leave that straddles the edge of a quarter is still leave.
 */
export function usePositionedBars(bars: LeaveTimelineBar[], days: TimelineDay[]) {
  return useMemo<Map<string | number, PositionedBar[]>>(() => {
    const byRow = new Map<string | number, PositionedBar[]>()

    if (days.length === 0) {
      return byRow
    }

    const columnByKey = new Map<string, number>()
    days.forEach((day, index) => columnByKey.set(day.key, index + 1))

    const windowStart = days[0].date
    const windowEnd = days[days.length - 1].date

    bars.forEach((bar) => {
      const barStart = dayjs(bar.startDate).startOf("day")
      const barEnd = dayjs(bar.endDate).startOf("day")

      if (!barStart.isValid() || !barEnd.isValid() || barEnd.isBefore(barStart)) {
        return
      }

      if (barEnd.isBefore(windowStart) || barStart.isAfter(windowEnd)) {
        return
      }

      const clippedStart = barStart.isBefore(windowStart)
      const clippedEnd = barEnd.isAfter(windowEnd)

      const effectiveStart = clippedStart ? windowStart : barStart
      const effectiveEnd = clippedEnd ? windowEnd : barEnd

      const columnStart = columnByKey.get(effectiveStart.format("YYYY-MM-DD"))
      if (!columnStart) {
        return
      }

      const span = effectiveEnd.diff(effectiveStart, "day") + 1

      const positioned: PositionedBar = {
        ...bar,
        columnStart,
        span,
        clippedStart,
        clippedEnd,
      }

      const existing = byRow.get(bar.rowId)
      if (existing) {
        existing.push(positioned)
      } else {
        byRow.set(bar.rowId, [positioned])
      }
    })

    return byRow
  }, [bars, days])
}
